/**
 * Hook to access section permissions from the /access endpoint.
 * Data is fetched after login and stored in FeatureAccessContext.
 *
 * Usage:
 *   const perms = usePermissions();
 *
 *   // Direct access to section permissions
 *   if (perms?.calendar?.canManageTimeBlocks) { ... }
 *   if (perms?.clients?.canDelete) { ... }
 *
 *   // Helper methods for cleaner code
 *   if (perms.can('clients', 'canDelete')) { ... }
 *   const scope = perms.getScope('calendar'); // 'own' | 'all' | 'none'
 *   if (perms.hasAccess('marketing')) { ... }
 *   if (perms.isAvailable('marketing')) { ... } // Check subscription tier
 */

import { useCallback, useMemo } from "react";
import { useFeatureAccess } from "../context/FeatureAccessContext";
import { SectionPermissions, SectionKey } from "../types/featureAccess";

export type ScopeResult = "none" | "own" | "all";

interface PermissionHelpers {
  /** All section permissions from /access endpoint */
  permissions: SectionPermissions | null;
  /** Check a specific permission within a section */
  can: <K extends SectionKey>(
    section: K,
    permission: keyof SectionPermissions[K],
  ) => boolean;
  /** Get scope for a section (own, all, or none) */
  getScope: (section: SectionKey) => ScopeResult;
  /** Check if user has any access to a section */
  hasAccess: (section: SectionKey) => boolean;
  /** Check if user can view a section */
  canView: (section: SectionKey) => boolean;
  /** Check if user can edit within a section */
  canEdit: (section: SectionKey) => boolean;
  /** Check if a workflow is available based on subscription tier */
  isAvailable: (section: SectionKey) => boolean;
  /** Check if user can view all staff (settingsStaff.scope === 'all') */
  canViewAllStaff: () => boolean;
  /** Check if user can view all calendar data (calendar.scope === 'all') */
  canViewAllCalendar: () => boolean;
  /** Check if user can view all services (settingsServices.scope === 'all') */
  canViewAllServices: () => boolean;
}

/**
 * Return type for usePermissions hook.
 * Provides helper methods as the primary API, with raw permissions accessible via .permissions
 * Note: For backward compatibility, section permissions are also spread onto the object,
 * but the helper methods (can, getScope, hasAccess, etc.) should be preferred.
 */
export type UsePermissionsReturn = PermissionHelpers &
  Partial<SectionPermissions>;

export function usePermissions(): UsePermissionsReturn {
  const { access } = useFeatureAccess();
  const permissions = access?.section_permissions ?? null;

  const can = useCallback(
    <K extends SectionKey>(
      section: K,
      permission: keyof SectionPermissions[K],
    ): boolean => {
      if (!permissions) return false;
      const sectionPerms = permissions[section];
      if (!sectionPerms) return false;
      const value = sectionPerms[permission];
      return typeof value === "boolean" ? value : false;
    },
    [permissions],
  );

  const getScope = useCallback(
    (section: SectionKey): ScopeResult => {
      if (!permissions) return "none";
      const sectionPerms = permissions[section];
      if (!sectionPerms) return "none";

      // Check for scope property
      if ("scope" in sectionPerms) {
        const scope = (sectionPerms as { scope?: string }).scope;
        if (scope === "all" || scope === "own" || scope === "none") {
          return scope;
        }
      }

      // Fall back to access property
      if ("access" in sectionPerms) {
        const access = (sectionPerms as { access?: string }).access;
        if (access === "all") return "all";
        if (access === "own" || access === "view") return "own";
        if (access === "none") return "none";
      }

      return "none";
    },
    [permissions],
  );

  const hasAccess = useCallback(
    (section: SectionKey): boolean => {
      if (!permissions) return false;
      const sectionPerms = permissions[section];
      if (!sectionPerms) return false;

      // Check for canView, canSee, canAccess, or canSeeOverview
      if (
        "canView" in sectionPerms &&
        (sectionPerms as { canView?: boolean }).canView
      ) {
        return true;
      }
      if (
        "canSee" in sectionPerms &&
        (sectionPerms as { canSee?: boolean }).canSee
      ) {
        return true;
      }
      if (
        "canAccess" in sectionPerms &&
        (sectionPerms as { canAccess?: boolean }).canAccess
      ) {
        return true;
      }
      if (
        "canSeeOverview" in sectionPerms &&
        (sectionPerms as { canSeeOverview?: boolean }).canSeeOverview
      ) {
        return true;
      }

      // Check access property
      if ("access" in sectionPerms) {
        const access = (sectionPerms as { access?: string }).access;
        return access !== "none";
      }

      // Check scope - if scope is not 'none', user has some access
      const scope = getScope(section);
      return scope !== "none";
    },
    [permissions, getScope],
  );

  const canView = useCallback(
    (section: SectionKey): boolean => {
      if (!permissions) return false;
      const sectionPerms = permissions[section];
      if (!sectionPerms) return false;

      // Check for canView property
      if ("canView" in sectionPerms) {
        return (sectionPerms as { canView?: boolean }).canView ?? false;
      }
      // Check for canSee property (marketing)
      if ("canSee" in sectionPerms) {
        return (sectionPerms as { canSee?: boolean }).canSee ?? false;
      }
      // Check for canAccess property
      if ("canAccess" in sectionPerms) {
        return (sectionPerms as { canAccess?: boolean }).canAccess ?? false;
      }
      // Check for canSeeOverview property (payments)
      if ("canSeeOverview" in sectionPerms) {
        return (
          (sectionPerms as { canSeeOverview?: boolean }).canSeeOverview ?? false
        );
      }

      return hasAccess(section);
    },
    [permissions, hasAccess],
  );

  const canEdit = useCallback(
    (section: SectionKey): boolean => {
      if (!permissions) return false;
      const sectionPerms = permissions[section];
      if (!sectionPerms) return false;

      // Check for canEdit property
      if ("canEdit" in sectionPerms) {
        return (sectionPerms as { canEdit?: boolean }).canEdit ?? false;
      }
      // Check for canCreate property (marketing)
      if ("canCreate" in sectionPerms) {
        return (sectionPerms as { canCreate?: boolean }).canCreate ?? false;
      }
      // Check for canManageServices property (settingsServices)
      if ("canManageServices" in sectionPerms) {
        return (
          (sectionPerms as { canManageServices?: boolean }).canManageServices ??
          false
        );
      }

      return false;
    },
    [permissions],
  );

  const isAvailable = useCallback(
    (section: SectionKey): boolean => {
      // Fail-closed: if permissions haven't loaded, assume not available
      // This prevents showing subscription-gated features before we know the user's tier
      if (!permissions) return false;
      const sectionPerms = permissions[section];
      if (!sectionPerms) return false;

      // Check for available property (new in workflow permissions)
      if ("available" in sectionPerms) {
        return (sectionPerms as { available?: boolean }).available === true;
      }

      // If no available flag, default to true (feature is not subscription-gated)
      return true;
    },
    [permissions],
  );

  const canViewAllStaff = useCallback((): boolean => {
    return getScope("settingsStaff") === "all";
  }, [getScope]);

  const canViewAllCalendar = useCallback((): boolean => {
    return getScope("calendar") === "all";
  }, [getScope]);

  const canViewAllServices = useCallback((): boolean => {
    return getScope("settingsServices") === "all";
  }, [getScope]);

  // Create the return object that includes both the permissions and helper methods
  // This allows backward compatible access like perms?.calendar?.canManageTimeBlocks
  // as well as helper methods like perms.can('calendar', 'canManageTimeBlocks')
  return useMemo(() => {
    const helpers: PermissionHelpers = {
      permissions,
      can,
      getScope,
      hasAccess,
      canView,
      canEdit,
      isAvailable,
      canViewAllStaff,
      canViewAllCalendar,
      canViewAllServices,
    };

    // If permissions is null, return just the helpers with null prototype chain
    if (!permissions) {
      return helpers as UsePermissionsReturn;
    }

    // Spread permissions onto the helpers object for backward compatibility
    return {
      ...permissions,
      ...helpers,
    } as UsePermissionsReturn;
  }, [
    permissions,
    can,
    getScope,
    hasAccess,
    canView,
    canEdit,
    isAvailable,
    canViewAllStaff,
    canViewAllCalendar,
    canViewAllServices,
  ]);
}

export default usePermissions;
